/**
 * Users Tab Component
 * Displays and manages whitelisted users
 */

import { useState, useEffect } from 'react';
import { Section } from '../../layout/Section';
import { AddUserModal } from './AddUserModal';
import {
  getWhitelistedUsers,
  addWhitelistedUser,
  deleteWhitelistedUser,
} from '../../../services/api/access-control';
import type { WhitelistedUser, CreateUserRequest } from '../../../types/access-control';

export function UsersTab() {
  const [users, setUsers] = useState<WhitelistedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);

  // Fetch users on mount
  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getWhitelistedUsers();
      setUsers(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (data: CreateUserRequest) => {
    await addWhitelistedUser(data);
    await fetchUsers();
  };

  const handleDeleteUser = async (id: string, email: string) => {
    if (!confirm(`Are you sure you want to remove the whitelisted user "${email}"?`)) {
      return;
    }

    try {
      await deleteWhitelistedUser(id);
      await fetchUsers();
    } catch (err) {
      alert(`Failed to delete user: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">Loading users...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        <p className="font-semibold">Error loading users</p>
        <p className="text-sm mt-1">{error}</p>
        <button
          onClick={fetchUsers}
          className="mt-2 text-sm text-red-800 underline hover:no-underline"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-gray-600">
          {users.length} whitelisted {users.length === 1 ? 'user' : 'users'}
        </p>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 text-sm font-semibold bg-indigo-600 text-white rounded hover:bg-indigo-700"
        >
          + Add User
        </button>
      </div>

      {users.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded p-8 text-center">
          <p className="text-gray-600 mb-2">No whitelisted users yet</p>
          <p className="text-sm text-gray-500">
            Add individual users to grant them access regardless of their email domain
          </p>
        </div>
      ) : (
        <Section title="Whitelisted Users">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr>
                  <th className="bg-indigo-950 text-indigo-100 px-3 py-2 font-semibold text-left">
                    Email
                  </th>
                  <th className="bg-indigo-950 text-indigo-100 px-3 py-2 font-semibold text-left">
                    Reason
                  </th>
                  <th className="bg-indigo-950 text-indigo-100 px-3 py-2 font-semibold text-left">
                    Added By
                  </th>
                  <th className="bg-indigo-950 text-indigo-100 px-3 py-2 font-semibold text-left">
                    Date
                  </th>
                  <th className="bg-indigo-950 text-indigo-100 px-3 py-2 font-semibold text-center">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.map((user, index) => (
                  <tr
                    key={user.id}
                    className={index % 2 === 0 ? 'bg-purple-50' : 'bg-white'}
                  >
                    <td className="px-3 py-2 border-b border-gray-200 text-left font-medium">
                      {user.email}
                    </td>
                    <td className="px-3 py-2 border-b border-gray-200 text-left">
                      <span className="text-gray-600">
                        {user.reason || (
                          <em className="text-gray-400">No reason provided</em>
                        )}
                      </span>
                    </td>
                    <td className="px-3 py-2 border-b border-gray-200 text-left">
                      <span className="text-gray-600">
                        {user.addedBy || (
                          <em className="text-gray-400">Unknown</em>
                        )}
                      </span>
                    </td>
                    <td className="px-3 py-2 border-b border-gray-200 text-left">
                      <span className="text-gray-600">{formatDate(user.createdAt)}</span>
                    </td>
                    <td className="px-3 py-2 border-b border-gray-200 text-center">
                      <button
                        onClick={() => handleDeleteUser(user.id, user.email)}
                        className="text-red-600 hover:text-red-800 font-medium"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {showAddModal && (
        <AddUserModal
          onClose={(saved) => {
            setShowAddModal(false);
            if (saved) fetchUsers();
          }}
          onSubmit={handleAddUser}
        />
      )}
    </div>
  );
}
